import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import EventRepeatIcon from "@mui/icons-material/EventRepeatOutlined";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { get, post } from "aws-amplify/api";
import Moment from "moment";
import StepUpDialog from "../components/StepUpDialog";

const CATEGORY_OPTIONS = ["Standard", "Trial", "Enterprise"];

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

// Add one calendar year to a yyyy-MM-dd string. Clamps Feb 29 to Feb 28
// in non-leap target years (no off-by-one March 1 from native Date).
function extendByOneYear(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const newY = y + 1;
  const daysInTargetMonth = new Date(newY, m, 0).getDate();
  const newD = Math.min(d, daysInTargetMonth);
  return `${newY}-${String(m).padStart(2, "0")}-${String(newD).padStart(2, "0")}`;
}

function arraysDiffer(a, b) {
  const sa = [...(a ?? [])].sort();
  const sb = [...(b ?? [])].sort();
  if (sa.length !== sb.length) return true;
  return sa.some((v, i) => v !== sb[i]);
}

async function readErrorBody(error) {
  const candidates = [
    error?.response?.body,
    error?.response,
    error?.underlyingError?.response?.body,
    error?.cause?.response?.body,
    error?.body,
  ];
  const tryRead = async (src) => {
    if (src == null) return null;
    if (typeof src === "object" && (src.error || src.detail || src.reference)) return src;
    if (typeof src?.json === "function") {
      try {
        return await Promise.race([
          src.json(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
      } catch (_) { /* */ }
    }
    if (typeof src?.text === "function") {
      try {
        const text = await Promise.race([
          src.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        if (typeof text === "string" && text) return JSON.parse(text);
      } catch (_) { /* */ }
    }
    if (typeof src === "string" && src) {
      try { return JSON.parse(src); } catch (_) { return null; }
    }
    return null;
  };
  for (const c of candidates) {
    const parsed = await tryRead(c);
    if (parsed) return parsed;
  }
  return null;
}

export default function LicenseList() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);   // { row, draft }
  const [actionInProgress, setActionInProgress] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  // pendingRetry holds a closure to re-run the same write call once
  // the step-up dialog returns success.
  const [pendingRetry, setPendingRetry] = useState(null);
  // viewKeys = { licenseId, licensedTo, licenseEndDate, licenseKeyV2,
  //              cmdLineLicenseKeyV2 } | null
  const [viewKeys, setViewKeys] = useState(null);
  const [keysLoading, setKeysLoading] = useState(false);
  // confirmExtend = { row, newEndDate } | null
  const [confirmExtend, setConfirmExtend] = useState(null);

  const fetchLicenses = async () => {
    setIsLoading(true);
    setError("");
    try {
      const op = get({ apiName: "license", path: "/licenses" });
      const { body } = await op.response;
      const result = await body.json();
      setLicenses(Array.isArray(result?.licenses) ? result.licenses : []);
    } catch (e) {
      setError(e?.message ?? "Failed to load licences");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return licenses;
    return licenses.filter((l) =>
      [l.licenseId, l.licensedTo].some((v) => (v ?? "").toLowerCase().includes(term))
    );
  }, [licenses, search]);

  // onSuccess runs after the licence row updates successfully. It must
  // be threaded through here (rather than handled by the caller post-
  // return) so it survives step-up retry — pendingRetry re-runs this
  // function, and any caller-side success handling would be lost
  // otherwise (the edit dialog would stay open after a successful
  // retry).
  const updateLicense = async (licenseId, patch, successMessage, onSuccess) => {
    setActionInProgress(true);
    try {
      const op = post({
        apiName: "license",
        path: "/licenses/update",
        options: { body: { licenseId, ...patch } }
      });
      const { body } = await op.response;
      const updated = await body.json();
      const merge = Object.fromEntries(
        Object.entries(updated).filter(([, v]) => v !== null && v !== undefined)
      );
      setLicenses((prev) =>
        prev.map((l) => (l.licenseId === licenseId ? { ...l, ...merge } : l))
      );
      const fullMessage = updated?.keysRegenerated
        ? `${successMessage} · keys regenerated`
        : successMessage;
      setSnackbar({ severity: "success", message: fullMessage });
      if (onSuccess) onSuccess();
      return { ok: true };
    } catch (e) {
      const errorBody = await readErrorBody(e);
      // Step-up required: stash a retry closure for the dialog and let
      // the caller know we're waiting (so it doesn't close its own
      // dialog or show a confusing error).
      if (errorBody?.error === "STEP_UP_REQUIRED") {
        setPendingRetry(() => () => updateLicense(licenseId, patch, successMessage, onSuccess));
        return { ok: false, stepUp: true };
      }
      let message = errorBody?.detail ?? e?.message ?? "Update failed";
      if (errorBody?.error === "LICENSE_NOT_FOUND") {
        message = "Licence not found — refresh the list.";
      } else if (errorBody?.error === "DOMAIN_TAKEN") {
        message = errorBody.detail ?? "An email domain is already mapped to a different licence.";
      }
      setSnackbar({ severity: "error", message });
      return { ok: false };
    } finally {
      setActionInProgress(false);
    }
  };

  const fetchKeys = async (row) => {
    setKeysLoading(true);
    try {
      const op = post({
        apiName: "license",
        path: "/licenses/keys",
        options: { body: { licenseId: row.licenseId } }
      });
      const { body } = await op.response;
      const data = await body.json();
      setViewKeys(data);
      return { ok: true };
    } catch (e) {
      const errorBody = await readErrorBody(e);
      const msg = errorBody?.detail ?? errorBody?.error ?? e?.message ?? "Failed to load keys";
      setSnackbar({ severity: "error", message: msg });
      return { ok: false };
    } finally {
      setKeysLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ severity: "success", message: `${label} copied` });
    } catch (_) {
      setSnackbar({ severity: "error", message: "Copy failed — your browser may have blocked clipboard access." });
    }
  };

  const handleExtend = (row) => {
    if (!row.licenseEndDate) {
      setSnackbar({ severity: "error", message: "Licence has no end date to extend." });
      return;
    }
    setConfirmExtend({
      row,
      newEndDate: extendByOneYear(row.licenseEndDate),
    });
  };

  const performExtend = async () => {
    if (!confirmExtend) return;
    const { row, newEndDate } = confirmExtend;
    setConfirmExtend(null);
    await updateLicense(
      row.licenseId,
      { licenseEndDate: newEndDate },
      `${row.licenseId} extended to ${newEndDate}`
    );
  };

  const openEdit = (row) => {
    setEditTarget({
      row,
      draft: {
        licensedTo:       row.licensedTo ?? "",
        licenseEndDate:   row.licenseEndDate ?? "",
        assignedLicenses: row.assignedLicenses ?? 0,
        category:         row.category ?? "Standard",
        selfRegistration: Boolean(row.selfRegistration),
        latestVersion:    row.latestVersion ?? "",
        emailDomains:     [...(row.emailDomains ?? [])]
      }
    });
  };

  const closeEdit = () => {
    if (!actionInProgress) setEditTarget(null);
  };

  const saveEdit = async () => {
    const { row, draft } = editTarget;

    // Validate domains client-side so we don't round-trip on an obvious typo.
    for (const d of draft.emailDomains) {
      if (!DOMAIN_RE.test(d)) {
        setSnackbar({ severity: "error", message: `'${d}' is not a valid hostname.` });
        return;
      }
    }

    const patch = {};
    if (draft.licensedTo !== row.licensedTo) patch.licensedTo = draft.licensedTo;
    if (draft.licenseEndDate !== row.licenseEndDate) patch.licenseEndDate = draft.licenseEndDate;
    if (Number(draft.assignedLicenses) !== row.assignedLicenses) patch.assignedLicenses = Number(draft.assignedLicenses);
    if (draft.category !== row.category) patch.category = draft.category;
    if (Boolean(draft.selfRegistration) !== Boolean(row.selfRegistration)) patch.selfRegistration = Boolean(draft.selfRegistration);
    if ((draft.latestVersion ?? "") !== (row.latestVersion ?? "")) patch.latestVersion = (draft.latestVersion ?? "").trim();
    if (arraysDiffer(draft.emailDomains, row.emailDomains)) patch.emailDomains = draft.emailDomains;

    if (Object.keys(patch).length === 0) {
      setEditTarget(null);
      return;
    }

    // Closing the edit dialog is passed in as the success callback so it
    // also fires on the step-up retry. Without that, a successful retry
    // would leave the dialog open even though the row had already saved.
    await updateLicense(
      row.licenseId,
      patch,
      `${row.licenseId} saved`,
      () => setEditTarget(null)
    );
  };

  const setDraft = (key, value) => {
    setEditTarget((prev) => prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, alignItems: { sm: "center" } }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            Licences
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {licenses.length} {licenses.length === 1 ? "organisation" : "organisations"}
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => navigate("/admin/organizations/new")}
        >
          New organisation
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      {!isLoading && licenses.length > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mb: 2, alignItems: { sm: "center" } }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by id or licensed to"
            size="small"
            sx={{ flex: 1, maxWidth: { sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </Stack>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Licence id</TableCell>
                <TableCell>Licensed to</TableCell>
                <TableCell>End date</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Latest version</TableCell>
                <TableCell>Domains</TableCell>
                <TableCell>Self-reg</TableCell>
                <TableCell align="right" width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={9}><Skeleton variant="text" /></TableCell>
                  </TableRow>
                ))
              ) : licenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    No licences yet.
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    No licences match "<b>{search}</b>".
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => {
                  const allocated = row.allocatedLicenses ?? 0;
                  const assigned = row.assignedLicenses ?? 0;
                  const overSubscribed = allocated > assigned;
                  return (
                    <TableRow key={row.licenseId} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="View licence keys">
                            <span>
                              <IconButton
                                size="small"
                                disabled={keysLoading}
                                onClick={() => fetchKeys(row)}
                                sx={{ color: "text.secondary" }}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Box>{row.licenseId}</Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{row.licensedTo}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {row.licenseEndDate ? Moment(row.licenseEndDate).format("MMM D, YYYY") : "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography component="span" sx={{ fontWeight: 500, color: overSubscribed ? "error.main" : "text.primary" }}>
                          {allocated}
                        </Typography>
                        <Typography component="span" sx={{ color: "text.secondary" }}>
                          {" / "}{assigned}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.category ?? "—"} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {row.latestVersion ? (
                          <Chip label={row.latestVersion} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <DomainsCell domains={row.emailDomains} />
                      </TableCell>
                      <TableCell>
                        {row.selfRegistration ? (
                          <Chip label="On" size="small" color="success" sx={{ fontWeight: 500 }} />
                        ) : (
                          <Typography variant="body2" color="text.disabled">Off</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Extend by 1 year">
                          <span>
                            <IconButton
                              size="small"
                              disabled={actionInProgress || !row.licenseEndDate}
                              onClick={() => handleExtend(row)}
                            >
                              <EventRepeatIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              disabled={actionInProgress}
                              onClick={() => openEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={Boolean(editTarget)} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Edit licence
          {editTarget ? (
            <Typography component="div" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              {editTarget.row.licenseId}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          {editTarget ? (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Licensed to"
                fullWidth
                value={editTarget.draft.licensedTo}
                onChange={(e) => setDraft("licensedTo", e.target.value)}
              />
              <TextField
                label="End date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editTarget.draft.licenseEndDate}
                onChange={(e) => setDraft("licenseEndDate", e.target.value)}
              />
              <TextField
                label="Assigned licences"
                type="number"
                fullWidth
                inputProps={{ min: 1 }}
                value={editTarget.draft.assignedLicenses}
                onChange={(e) => setDraft("assignedLicenses", e.target.value)}
              />
              <TextField
                select
                label="Category"
                fullWidth
                value={editTarget.draft.category}
                onChange={(e) => setDraft("category", e.target.value)}
              >
                {CATEGORY_OPTIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                label="Latest version"
                placeholder="e.g. 1.4.2"
                fullWidth
                value={editTarget.draft.latestVersion}
                onChange={(e) => setDraft("latestVersion", e.target.value)}
                helperText="Leave blank to clear."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(editTarget.draft.selfRegistration)}
                    onChange={(e) => setDraft("selfRegistration", e.target.checked)}
                  />
                }
                label="Allow self-registration from licensed email domains"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Email domains. Press Enter or comma after each. Removing a domain unmaps it from this licence.
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={editTarget.draft.emailDomains}
                  onChange={(_, v) => setDraft("emailDomains", v.map((d) => d.trim().toLowerCase()).filter(Boolean))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={editTarget.draft.emailDomains.length === 0 ? "acme.com" : ""}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: "new-password",
                        "data-form-type": "other"
                      }}
                    />
                  )}
                />
              </Box>
              <Alert severity="info" sx={{ "& .MuiAlert-message": { fontSize: "0.85rem" } }}>
                Changing <strong>Licensed to</strong> or <strong>End date</strong> regenerates the signed licence keys. The desktop client picks up the new keys on its next /license fetch.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} disabled={actionInProgress}>Cancel</Button>
          <Button onClick={saveEdit} variant="contained" disabled={actionInProgress}>
            {actionInProgress ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Dialog
        open={Boolean(confirmExtend)}
        onClose={() => !actionInProgress && setConfirmExtend(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Extend licence by 1 year?</DialogTitle>
        <DialogContent>
          {confirmExtend ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.primary">
                <strong>{confirmExtend.row.licenseId}</strong> ({confirmExtend.row.licensedTo})
              </Typography>
              <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  Current
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {confirmExtend.row.licenseEndDate
                    ? Moment(confirmExtend.row.licenseEndDate).format("MMM D, YYYY")
                    : "—"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  New
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                  {Moment(confirmExtend.newEndDate).format("MMM D, YYYY")}
                </Typography>
              </Stack>
              <Alert severity="info" sx={{ mt: 1, "& .MuiAlert-message": { fontSize: "0.8rem" } }}>
                The signed licence keys will be regenerated. The desktop client picks up the new keys on its next /license fetch.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmExtend(null)} disabled={actionInProgress}>Cancel</Button>
          <Button onClick={performExtend} variant="contained" disabled={actionInProgress}>
            {actionInProgress ? "Extending…" : "Extend"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewKeys)} onClose={() => setViewKeys(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Licence keys
          {viewKeys ? (
            <Typography component="div" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              {viewKeys.licensedTo} · {viewKeys.licenseId} · expires {viewKeys.licenseEndDate}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          {viewKeys ? (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <KeyBlock
                label="LicenseKeyV2 (full product)"
                value={viewKeys.licenseKeyV2}
                onCopy={() => copyToClipboard(viewKeys.licenseKeyV2, "Full product key")}
              />
              <KeyBlock
                label="CmdLineLicenseKeyV2 (cmdline tool)"
                value={viewKeys.cmdLineLicenseKeyV2}
                onCopy={() => copyToClipboard(viewKeys.cmdLineLicenseKeyV2, "Cmdline key")}
              />
              <Alert severity="info" sx={{ "& .MuiAlert-message": { fontSize: "0.85rem" } }}>
                These keys are signed against {viewKeys.licensedTo} and {viewKeys.licenseEndDate}. If either changes, the keys are regenerated and these become invalid.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewKeys(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <StepUpDialog
        open={Boolean(pendingRetry)}
        onClose={() => setPendingRetry(null)}
        onVerified={async () => {
          const fn = pendingRetry;
          setPendingRetry(null);
          if (fn) await fn();
        }}
      />
    </Box>
  );
}

function KeyBlock({ label, value, onCopy }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Copy">
          <IconButton size="small" onClick={onCopy} disabled={!value}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
          fontSize: "0.78rem",
          lineHeight: 1.4,
          py: 1.5,
          px: 1.5,
          bgcolor: "action.hover",
          borderRadius: 1,
          maxHeight: 180,
          overflow: "auto",
          wordBreak: "break-all"
        }}
      >
        {value || "—"}
      </Box>
    </Box>
  );
}

// Compact one-line cell for a licence's email-domain list. Shows the
// first domain inline; if there are more, an outlined "+N" chip
// summarises the rest and reveals them in a tooltip on hover.
function DomainsCell({ domains }) {
  if (!domains?.length) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  const [first, ...rest] = domains;
  const chipSx = { height: 22, fontSize: "0.72rem", maxWidth: 160 };
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Tooltip title={first} placement="top">
        <Chip label={first} size="small" variant="outlined" sx={chipSx} />
      </Tooltip>
      {rest.length > 0 ? (
        <Tooltip title={rest.join(", ")} placement="top" arrow>
          <Chip
            label={`+${rest.length}`}
            size="small"
            sx={{ ...chipSx, cursor: "help", fontWeight: 600 }}
          />
        </Tooltip>
      ) : null}
    </Stack>
  );
}
